import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, defer, firstValueFrom, from, interval, map, mergeMap, Observable, of, Subscription } from 'rxjs';
import { environment } from "../../../environments/environment";
import {
  ApiResponse,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  PasswordResetTokenValidationResponse,
  ResetPasswordRequest,
  SignUpRequest,
  UserAccountDTO
} from "../../shared/object/xplorer-be-parsed-classes";
import { TokenStorageService } from "./token-storage.service";
import { KeycloakService } from "keycloak-angular";
import { UserService } from "../user/user.service";
import { ToastService } from "../toast/toast.service";
import { ToastTextType } from "../../shared/object/toast";
import { KeycloakSessionService } from "./keycloak-session.service";

type KeycloakProfile = {
  id?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

const httpOptions = {
  headers: new HttpHeaders({'Content-Type': 'application/json'})
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly httpClientNoInterceptor: HttpClient;
  private readonly isBrowser: boolean;

  private sessionHandlingInitialized = false;
  private sessionWatcher?: Subscription;
  private handlingSessionExpiry = false;

  constructor(
    private readonly http: HttpClient,
    private readonly tokenStorage: TokenStorageService,
    private readonly keycloakService: KeycloakService,
    private readonly userService: UserService,
    private readonly toastService: ToastService,
    private readonly keycloakSession: KeycloakSessionService,
    httpBackend: HttpBackend,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.httpClientNoInterceptor = new HttpClient(httpBackend);
    this.isBrowser = isPlatformBrowser(platformId);
  }

  googleLogin = (source: string): Observable<void> => {
    this.clearTokenStorage();
    const redirectUrl = `${this.getBaseUrl()}/load-user${source ? `?source=${encodeURIComponent(source)}` : ''}`;
    return from(this.keycloakService.login({
      idpHint: 'google',
      redirectUri: redirectUrl
    }));
  }

  login(email: string, password: string): Observable<LoginResponse> {
    const loginRequest: LoginRequest = {
      password,
      email,
    };
    return this.httpClientNoInterceptor.post<LoginResponse>(environment.API_URL + '/auth/signin', loginRequest, httpOptions);
  }

  applyLoginResponse(loginResponse: LoginResponse): void {
    console.debug('[Auth] Applying login response session:', {
      hasAccessToken: !!loginResponse?.token,
      hasRefreshToken: !!loginResponse?.refreshToken,
      hasUser: !!loginResponse?.user
    });
    this.persistAuthenticatedSession(loginResponse.token, loginResponse.refreshToken, loginResponse.user);
  }

  forgotPassword(email: string): Observable<ApiResponse> {
    const payload: ForgotPasswordRequest = {
      email,
      source: this.resolveFrontendSource()
    };
    return this.http.post<ApiResponse>(environment.API_URL + '/auth/password/forgot', payload, httpOptions);
  }

  validateResetToken(token: string): Observable<PasswordResetTokenValidationResponse> {
    return this.http.get<PasswordResetTokenValidationResponse>(`${environment.API_URL}/auth/password/reset/${token}`, httpOptions);
  }

  resetPassword(request: ResetPasswordRequest): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(environment.API_URL + '/auth/password/reset', request, httpOptions);
  }

  logout(redirectUrl = `${environment.FRONTEND_BASE_URL}/login`): void {
    console.debug('[Auth] Logging out with keycloak redirect:', { redirectUrl });
    this.clearTokenStorage();
    this.toastService.showSuccessToast(ToastTextType.LOGOUT_SUCCESSFUL);

    this.keycloakService.logout(redirectUrl)
      .catch(error => {
        console.error('[Auth] Error while logging out', error);
        this.keycloakSession.clearSession();
        if (this.isBrowser) {
          window.location.href = redirectUrl;
        }
      });
  }

  saveUserInfo = (source: Promise<KeycloakProfile> | Observable<KeycloakProfile>): Observable<UserAccountDTO> => {
    return from(source)
      .pipe(
        map(userProfile => {
          const userRoles = this.getUserRoles();
          const firstName = userProfile.firstName ?? '';
          const lastName = userProfile.lastName ?? '';
          const userInfo: UserAccountDTO = {
            userId: null,
            keycloakId: userProfile.id ?? '',
            username: userProfile.username ?? '',
            firstName,
            lastName,
            displayedName: `${firstName} ${lastName}`.trim(),
            email: userProfile.email ?? '',
            roles: userRoles,
            experience: 0,
            reputation: 0,
            coins: 0,
            instagramUsername: null,
            ugcProgramStatus: 'WAITING'
          };
          return userInfo;
        }),
        mergeMap(userInfo => userInfo.email ? this.userService.upsertUserFromKeycloak(userInfo) : of(userInfo))
      );
  }

  register(signUpRequest: SignUpRequest): Observable<ApiResponse> {
    signUpRequest.authProvider = 'LOCAL';
    return this.httpClientNoInterceptor.post<ApiResponse>(environment.AUTH_API_URL + '/signup', signUpRequest, httpOptions);
  }

  isUserLogged = (): Observable<boolean> => {
    if (!this.isBrowser) {
      return of(false);
    }

    console.debug('[Auth] isUserLogged() called');
    return defer(async () => {
      try {
        const keycloakLoggedIn = await Promise.resolve(this.keycloakService.isLoggedIn() as boolean | Promise<boolean>);
        console.debug('[Auth] Keycloak isLoggedIn:', keycloakLoggedIn);

        if (!keycloakLoggedIn) {
          this.clearTokenStorage();
          return false;
        }

        const keycloakToken = await this.keycloakSession.getValidToken(0, 'Token expired during login state check');
        console.debug('[Auth] Keycloak token present:', !!keycloakToken);

        if (!keycloakToken) {
          console.debug('[Auth] Token unavailable after sync/refresh — signing out');
          this.clearTokenStorage();
          return false;
        }

        console.debug('[Auth] isUserLogged result: true');
        return true;
      } catch {
        const localToken = this.tokenStorage.getToken();
        const result = !!localToken && !this.tokenStorage.isTokenExpired(localToken);
        console.debug('[Auth] Keycloak unavailable, fallback to localStorage:', result);
        return result;
      }
    }).pipe(
      catchError(() => of(false))
    );
  }

  getUserRoles = (): string[] => {
    const token = this.tokenStorage.getToken();
    if (token === null || token === undefined) {
      return [];
    }
    return this.tokenStorage.getRolesFromToken(token);
  }

  getUser(): UserAccountDTO | null {
    return this.tokenStorage.getUser();
  }

  getToken(): string | null {
    return this.tokenStorage.getToken();
  }

  completeKeycloakLogin = (): Observable<UserAccountDTO> => {
    return defer(async () => {
      console.debug('[Auth] Completing keycloak login callback');
      const token = await this.keycloakSession.syncTokensFromKeycloak();

      if (!token) {
        throw new Error('[Auth] Missing keycloak token during callback completion');
      }

      this.setupKeycloakTokenExpiryHandler();

      const userInfo = await firstValueFrom(
        this.saveUserInfo(this.keycloakService.loadUserProfile())
      );

      this.tokenStorage.saveUser(userInfo);
      console.debug('[Auth] Keycloak login callback completed');
      return userInfo;
    });
  }

  initKeycloakWithNewToken = (token: string, refreshToken: string) => {
    console.debug('[Auth] Initializing keycloak session with fresh login response:', {
      hasAccessToken: !!token,
      hasRefreshToken: !!refreshToken
    });
    this.persistAuthenticatedSession(token, refreshToken);
  }

  initializeSessionHandling(): void {
    if (!this.isBrowser) {
      return;
    }

    if (this.sessionHandlingInitialized) {
      console.debug('[Auth] Session handling already initialized');
      return;
    }

    this.sessionHandlingInitialized = true;
    console.debug('[Auth] Initializing session handling');
    this.setupKeycloakTokenExpiryHandler();

    this.sessionWatcher = interval(15000).subscribe(() => this.checkLocalTokenValidity());
    this.checkLocalTokenValidity();
  }

  clearTokenIfExpired = () => {
    this.checkLocalTokenValidity();
  }

  clearTokenStorage(): void {
    this.tokenStorage.signOut();
  }

  private persistAuthenticatedSession(token: string, refreshToken: string, user?: UserAccountDTO): void {
    if (user) {
      this.tokenStorage.saveUser(user);
    }

    this.keycloakSession.hydrateKeycloakSession(token, refreshToken);
    this.setupKeycloakTokenExpiryHandler();
  }

  private setupKeycloakTokenExpiryHandler(): void {
    console.debug('[Auth] Binding keycloak token expiry handler');
    this.keycloakSession.bindTokenExpiryHandler(reason => this.handleSessionExpired(reason));
  }

  private checkLocalTokenValidity(): void {
    const token = this.tokenStorage.getToken();

    if (!token) {
      return;
    }

    if (this.tokenStorage.isTokenExpired(token)) {
      void this.handleExpiredToken('Local token expired');
    }
  }

  private async handleExpiredToken(reason: string): Promise<void> {
    console.debug('[Auth] Handling expired token:', reason);
    const token = await this.keycloakSession.getValidToken(0, reason);
    if (!token) {
      this.handleSessionExpired(reason);
    }
  }

  private handleSessionExpired(reason: string): void {
    if (!this.isBrowser) {
      this.clearTokenStorage();
      return;
    }

    if (this.handlingSessionExpiry) {
      return;
    }

    this.handlingSessionExpiry = true;
    console.debug('[Auth] Session expired:', reason);
    this.clearTokenStorage();

    this.keycloakService.logout(`${environment.FRONTEND_BASE_URL}/login`)
      .catch(() => {
        this.keycloakSession.clearSession();
        window.location.href = `${environment.FRONTEND_BASE_URL}/login`;
      })
      .finally(() => {
        this.handlingSessionExpiry = false;
      });
  }

  private getBaseUrl() {
    if (typeof window === 'undefined') {
      return environment.FRONTEND_BASE_URL;
    }
    const parsedUrl = new URL(window.location.href);
    return parsedUrl.origin;
  }

  private resolveFrontendSource(): string | undefined {
    try {
      if (typeof window === 'undefined') {
        return undefined;
      }
      const hostname = window?.location?.hostname;
      if (!hostname) {
        return undefined;
      }

      const parts = hostname.split('.');
      if (parts.length < 3) {
        return undefined;
      }

      const candidate = parts[0]?.toLowerCase();
      if (!candidate || candidate === 'www') {
        return undefined;
      }

      return candidate;
    } catch (error) {
      console.warn('[Auth] Unable to resolve frontend source from hostname', error);
      return undefined;
    }
  }
}
